import React, { useState, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useBrandingColors } from '../hooks/useBrandingColors';

interface TagsInputProps {
  value?: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  showHint?: boolean;
  variant?: 'default' | 'skill';
}

const TagsInput: React.FC<TagsInputProps> = ({
  value = [],
  onChange,
  placeholder = 'Введите название и нажмите Enter',
  showHint = true,
  variant = 'default',
}) => {
  const colors = useBrandingColors();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const tags = Array.isArray(value) ? value : [];
  const isSkillVariant = variant === 'skill';

  const addTag = (tag: string) => {
    const t = tag.trim();
    if (t && !tags.includes(t)) {
      onChange([...tags, t]);
      setInputValue('');
      inputRef.current?.focus();
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(tags.filter(t => t !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className={isSkillVariant ? 'bg-white relative rounded-[10px] shrink-0 w-full' : 'relative'}>
      <div className={isSkillVariant
        ? 'box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full'
        : 'min-h-[42px] px-3 py-1.5 border border-[#cdd0d5] rounded-3xl bg-[#ffffff] hover:bg-[#f6f8fa]'}
      >
        <div className={isSkillVariant
          ? "basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#525866] text-[14px] text-left tracking-[-0.084px]"
          : ''}
        >
          <div className="flex flex-wrap gap-2 items-center min-h-[20px]">
          {tags.map((tag, index) => (
            <span
              key={index}
              className={isSkillVariant
                ? 'inline-flex items-center rounded-full text-[12px] px-2 py-1 whitespace-nowrap'
                : 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm'}
              style={{ backgroundColor: colors.primary + '20', color: colors.primary }}
            >
              {tag}
              <button type="button" onClick={() => removeTag(tag)} className={isSkillVariant ? 'ml-1 rounded-full p-0.5' : 'rounded-full p-0.5'} style={{ color: colors.primary }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primary + '40'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={tags.length === 0 ? placeholder : ''}
            className={isSkillVariant
              ? "flex-1 min-w-0 outline-none bg-transparent text-[14px] leading-[20px] text-[#525866] placeholder:text-[#868c98]"
              : 'flex-1 min-w-[150px] outline-none bg-transparent text-sm placeholder:text-xs placeholder:text-gray-400'}
          />
          </div>
        </div>
      </div>
      {isSkillVariant && (
        <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
      )}
      {showHint && tags.length === 0 && (
        <div className="mt-2 text-xs text-gray-500">Введите название и нажмите Enter для добавления</div>
      )}
    </div>
  );
};

export default TagsInput;
